import { UseFormReturn } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { v4 as uuidv4 } from 'uuid';
import { EnvironmentService } from '@services/EnvironmentService';
import { DictionariesService } from '@services/DictionariesService';
import { isIsoDateString } from '@utils/documentUtils';
import { parseRuDate, safeFormatDate } from '@utils/date';
import { indirectEval } from '@utils/scriptEval';
import { DocumentLinksService } from '@services/DocumentLinksService';
import { DocumentAttachService } from '@services/DocumentAttachService';
import { AxiosResponse } from 'axios';
import x2js from 'x2js';
import { sendNotification } from '@srm/ui/lib/notifications';
import { ConfigService } from '@/configuration/services/configService';
import { IConfig } from '@/configuration/models/IConfig';
import { IDebugSettings } from '@models/IDebugSettings';
import { isISODate } from '@utils/helpers';
import { loadMathJs } from '@utils/mathjsLoader';
import { EXTERNAL_DATA_SOURCE_PREFIX } from '@/utils/externalDataSourceUtils';
import type { IDocumentDataSource } from '@/services/documentDataSource/DocumentDataSource';
import { documentRemoteFormulaFunctions } from '@/services/documentDataSource/DocumentDataSource';
import type { IFormulaRequestCache } from './IFormulaRequestCache';
import { escapeRegExp } from '@utils/escapeRegExp';
import { formulaStringLiteral, substituteFormulaData } from './formulaStringLiteral';

export enum FieldType {
    Field,
    SumColumn,
    DictExist,
    Count,
    Fact,
    CheckRow,
    SetGuid,
    ParentField,
    GetDay,
    GetYear,
    GetMonth,
    OffsetDay,
    OffsetYear,
    OffsetMonth,
    OffsetWorkDay,
    Now,
    CurrentYear,
    NowOnlyDate,
    SplitAndGetByIndex,
    SplitAndGetCount,
    GetTableCell,
    GetTableCells,
    GetTableMaxCell,
    GetTableMinCell,
    GetDataFromSP,
    SetDate,
    SetTime,
    FormatStr,
    Length,
    StrToDate,
    GetLinksCount,
    GetAttachmentsCount,
    LastWorkDayAfterOffset,
    EscapeString,
}

export interface FormulaField {
    regex: RegExp;
    type: FieldType;
}

export interface IFormulaElem {
    index?: number;
    name: string;
    defValue: string | boolean | number | null | undefined | Date | any[];
    formulaPart: {
        [key: string]: string;
    };
    params: {
        [key: string]: string;
    };
    match: RegExpExecArray;
    type: FieldType;
}

const VARIABLES_REFERENCE = /\{variables\((.*?)\)\}|variables\((.*?)\)/;

const getRemoteFormulaFunctions = (formula?: string | null) =>
    documentRemoteFormulaFunctions.filter(
        (functionName) => !!formula && new RegExp(`\\b${functionName}\\s*\\(`, 'i').test(formula),
    );

/** Формулы, вычисление которых обращается к серверу и требует subject-scoped preview data source. */
export const hasRemoteFormulaData = (formula?: string | null): boolean => getRemoteFormulaFunctions(formula).length > 0;

export const hasUnsupportedRemoteFormulaData = (formula?: string | null, dataSource?: IDocumentDataSource): boolean =>
    getRemoteFormulaFunctions(formula).some((functionName) => !dataSource?.supportedFormulaFunctions.has(functionName));

type FactMode = 'any' | 'all' | 'count' | 'sum';

interface IFactDefinition {
    name: string;
    mode: FactMode;
    filter?: string;
    column?: string;
}

export const _formulaElems: FormulaField[] = [
    {
        regex: /FACT\((?<watch>.*?);;(?<name>.*?)\)/g,
        type: FieldType.Fact,
    },
    {
        regex: /CHECKROW\((?<watch>.*?);;(?<filter>.*?);;(?<param>.?any|all)\)/g,
        type: FieldType.CheckRow,
    },
    {
        regex: /SUMCOLUMN\((?<watch>.*?);;(?<param>.*?)(;;(?<filter>.*?))?\)/g,
        type: FieldType.SumColumn,
    },
    {
        regex: /DICTEXIST\((?<name>.*?);;(?<column>.*?);;(?<watch>.*?);;(?<return>.*?)(;;(?<type>.*?))?\)/g,
        type: FieldType.DictExist,
    },
    {
        regex: /GETDATAFROMSP\((?<name>.*?);;(?<watch>.*?)((^$)|(;;(?<param>.*?)))?((^$)|(;;(?<param1>.*?)))?((^$)|(;;(?<param2>.*?)))?((^$)|(;;(?<param3>.*?)))?((^$)|(;;(?<param4>.*?)))?\)/g,
        type: FieldType.GetDataFromSP,
    },
    {
        regex: /SPLITANDGETBYINDEX\((?<watch>.*?);;(?<separator>.*?);;(?<index>.*?)(;;(?<skipEmpty>.*?))?\)/g,
        type: FieldType.SplitAndGetByIndex,
    },
    {
        regex: /SPLITANDGETCOUNT\((?<watch>.*?);;(?<separator>.*?)(;;(?<skipEmpty>.*?))?\)/g,
        type: FieldType.SplitAndGetCount,
    },
    {
        regex: /GETTABLECELL\((?<watch>.*?);;(?<columnName>.*?);;(?<rowIndex>.*?)\)/g,
        type: FieldType.GetTableCell,
    },
    {
        regex: /GETTABLECELLS\((?<watch>.*?);;(?<columnName>.*?)(;;(?<separator>.*?))?\)/g,
        type: FieldType.GetTableCells,
    },
    {
        regex: /GETTABLEMAXCELL\((?<watch>.*?);;(?<columnName>.*?)\)/g,
        type: FieldType.GetTableMaxCell,
    },
    {
        regex: /GETTABLEMINCELL\((?<watch>.*?);;(?<columnName>.*?)\)/g,
        type: FieldType.GetTableMinCell,
    },
    {
        regex: /GETLINKSCOUNT\((?<tableKey>.*?)\)/g,
        type: FieldType.GetLinksCount,
    },
    {
        regex: /GETATTACHMENTSCOUNT\((?<tableKey>.*?)\)/g,
        type: FieldType.GetAttachmentsCount,
    },
    {
        regex: /COUNT\((?<watch>.*?)\)/g,
        type: FieldType.Count,
    },
    {
        regex: /LENGTH\((?<watch>.*?)\)/g,
        type: FieldType.Length,
    },
    {
        regex: /GETDAY\((?<watch>.*?)\)/g,
        type: FieldType.GetDay,
    },
    {
        regex: /GETYEAR\((?<watch>.*?)\)/g,
        type: FieldType.GetYear,
    },
    {
        regex: /GETMONTH\((?<watch>.*?)\)/g,
        type: FieldType.GetMonth,
    },
    {
        regex: /OFFSETDAY\((?<watch>.*?);;(?<param>.*?)?((^$)|(;;(?<param1>.*?)))?\)/g,
        type: FieldType.OffsetDay,
    },
    {
        regex: /OFFSETWORKDAY\((?<watch>.*?);;(?<param>.*?)?((^$)|(;;(?<param1>.*?)))?\)/g,
        type: FieldType.OffsetWorkDay,
    },
    {
        regex: /LASTWORKDAYAFTEROFFSET\((?<watch>.*?);;(?<offset>.*?);;(?<allowedWeekDays>.*?)\)/g,
        type: FieldType.LastWorkDayAfterOffset,
    },
    {
        regex: /OFFSETYEAR\((?<watch>.*?);;(?<param>.*?)\)/g,
        type: FieldType.OffsetYear,
    },
    {
        regex: /OFFSETMONTH\((?<watch>.*?);;(?<param>.*?)\)/g,
        type: FieldType.OffsetMonth,
    },
    {
        regex: /FORMATSTR\((?<watch>.*?);;(?<param>.*?)\)/g,
        type: FieldType.FormatStr,
    },
    {
        regex: /ESCAPESTRING\((?<watch>.*?)\)/g,
        type: FieldType.EscapeString,
    },
    {
        regex: /SETDATE\((?<watch>.*?);;(?<month>.*?);;(?<day>.*?);;(?<hour>.*?);;(?<minutes>.*?)\)/g,
        type: FieldType.SetDate,
    },
    {
        regex: /SETTIME\((?<watch>.*?);;(?<hour>.*?);;(?<minutes>.*?)\)/g,
        type: FieldType.SetTime,
    },
    {
        regex: /\{setGuid\}/g,
        type: FieldType.SetGuid,
    },
    {
        regex: /\{now\}/g,
        type: FieldType.Now,
    },
    // Legacy-алиасы для {now}, встречающиеся в конфигах Акрон/Русагро
    // (DopSogl.xml, Contract.xml). ReplaceFormulaValues ничего не делает
    // с буквальной строкой "now()" — она дальше идёт в new Date(+...) → NaN.
    {
        regex: /@now\(\)/g,
        type: FieldType.Now,
    },
    {
        regex: /\bnow\(\)/g,
        type: FieldType.Now,
    },
    {
        regex: /\{currentYear\}/g,
        type: FieldType.CurrentYear,
    },
    {
        regex: /STRTODATE\((?<strdate>.*?)\)/g,
        type: FieldType.StrToDate,
    },
    {
        regex: /\{nowOnlyDate\}/g,
        type: FieldType.NowOnlyDate,
    },
    {
        regex: /\{today\}/g,
        type: FieldType.NowOnlyDate,
    },
    {
        regex: /{(?<watch>\|Document.*?)\}/g,
        type: FieldType.Field,
    },
    {
        regex: /{(\|Parent(?<watch>.*?))\}/g,
        type: FieldType.ParentField,
    },
    {
        regex: /{(?!(?:isView|isEdit|isNew|')\b)(?<watch>.*?)}/g,
        type: FieldType.Field,
    },
];

export class FormulaManager {
    private _rules: string;
    private _isDebug: boolean;
    private _debugTag?: string;
    private _formula: string | undefined;
    private _preparedExpression: string | undefined;
    private _fields: IFormulaElem[] | undefined;
    private _formMethods: UseFormReturn<any> | undefined;
    private _parentRow: any | undefined;
    private _data: Record<string, IFieldElem> | undefined;
    private _dataSource?: IDocumentDataSource;
    private _requestCache?: IFormulaRequestCache;
    private _config: Readonly<IConfig>;

    private _boolExp: string[] = ['>', '<', '&&', '||', '==', '===', '!=', '!==', 'true', 'false', 'test'];

    private static _linkCountCache: { [id: string]: Promise<AxiosResponse<number, any>> | undefined } = {};
    private static _cacheDurationMs: number = 10000;
    private static _factsConfigByTable: Map<string, Map<string, IFactDefinition>> = new Map();
    private static _factsCacheByTable: Map<string, WeakMap<object, Map<string, any>>> = new Map();

    public static ConfigureFactsFromView(view?: any) {
        FormulaManager._factsConfigByTable.clear();
        FormulaManager._factsCacheByTable.clear();

        if (!view) {
            return;
        }

        const tables = FormulaManager.getFactsFromConfig(view);
        tables.forEach((table) => {
            let tableMap = FormulaManager._factsConfigByTable.get(table.tableKey);
            if (!tableMap) {
                tableMap = new Map();
                FormulaManager._factsConfigByTable.set(table.tableKey, tableMap);
            }
            table.facts.forEach((fact) => {
                tableMap!.set(fact.name, fact);
            });
        });
    }

    public static InvalidateFacts(tableKey?: string) {
        if (tableKey) {
            FormulaManager._factsCacheByTable.delete(tableKey);
        } else {
            FormulaManager._factsCacheByTable.clear();
        }
    }

    private static ensureArray<T>(value: T | T[] | undefined | null): T[] {
        if (value == null) return [];
        return Array.isArray(value) ? value : [value];
    }

    private static normalizeFactMode(mode?: string): FactMode | undefined {
        const normalized = mode?.trim().toLowerCase();
        if (normalized === 'any' || normalized === 'all' || normalized === 'count' || normalized === 'sum') {
            return normalized;
        }
        return undefined;
    }

    private static parseVariableFactDefinition(rawValue?: string): Omit<IFactDefinition, 'name'> | undefined {
        const value = rawValue?.trim();
        if (!value) {
            return {
                mode: 'any',
            };
        }

        if (value.startsWith('{') && value.endsWith('}')) {
            try {
                const parsed = JSON.parse(value);
                const mode = FormulaManager.normalizeFactMode(parsed?.mode);
                if (!mode) return undefined;
                const column = parsed?.column ? String(parsed.column) : undefined;
                if (mode === 'sum' && !column) return undefined;
                return {
                    mode: mode,
                    filter: parsed?.filter ? String(parsed.filter) : undefined,
                    column,
                };
            } catch {
                return undefined;
            }
        }

        const parts = value.split(';;');
        const firstPartMode = FormulaManager.normalizeFactMode(parts[0]);
        if (firstPartMode) {
            if (firstPartMode === 'sum') {
                const column = parts[1]?.trim() || undefined;
                const filter = parts.slice(2).join(';;') || undefined;

                if (!column) return undefined;

                return {
                    mode: firstPartMode,
                    column,
                    filter,
                };
            }

            return {
                mode: firstPartMode,
                filter: parts.slice(1).join(';;') || undefined,
            };
        }

        return {
            mode: 'any',
            filter: value,
        };
    }

    private static getFactsFromConfig(view: any): Array<{ tableKey: string; facts: IFactDefinition[] }> {
        const result: Array<{ tableKey: string; facts: IFactDefinition[] }> = [];

        const appendFact = (tableKey: string, fact: IFactDefinition) => {
            let table = result.find((x) => x.tableKey === tableKey);
            if (!table) {
                table = { tableKey, facts: [] };
                result.push(table);
            }

            const existing = table.facts.findIndex((x) => x.name === fact.name);
            if (existing >= 0) {
                table.facts[existing] = fact;
            } else {
                table.facts.push(fact);
            }
        };

        const factsNode = view?.facts;
        const factTables = FormulaManager.ensureArray(
            factsNode?.table ?? factsNode?.tables ?? factsNode?.tableFact ?? factsNode?.factTable,
        );

        factTables.forEach((tableNode: any) => {
            const tableKey = tableNode?.key ?? tableNode?.tableKey ?? tableNode?.watch;
            if (!tableKey) return;

            const factNodes = FormulaManager.ensureArray(tableNode?.fact ?? tableNode?.facts ?? tableNode?.rules);
            factNodes.forEach((factNode: any) => {
                const name = factNode?.name ?? factNode?.factName ?? factNode?.key;
                const mode = FormulaManager.normalizeFactMode(factNode?.mode ?? factNode?.type ?? 'any');
                if (!name || !mode) return;
                const column = factNode?.column ? String(factNode.column) : undefined;
                if (mode === 'sum' && !column) return;

                appendFact(tableKey, {
                    name: String(name),
                    mode,
                    filter: factNode?.filter ? String(factNode.filter) : undefined,
                    column,
                });
            });
        });

        const variables = FormulaManager.ensureArray(view?.variables?.variable);
        variables.forEach((variableNode: any) => {
            const variableName = String(variableNode?.variableName ?? '');
            if (!variableName) return;

            let tableKey: string | undefined;
            let factName: string | undefined;

            const colonMatch = /^fact:(?<table>.+?):(?<fact>.+)$/i.exec(variableName);
            if (colonMatch?.groups) {
                tableKey = colonMatch.groups.table;
                factName = colonMatch.groups.fact;
            }

            if (!tableKey || !factName) {
                return;
            }

            const parsed = FormulaManager.parseVariableFactDefinition(variableNode?.variableFormat);
            if (!parsed) return;

            appendFact(tableKey, {
                name: factName,
                mode: parsed.mode,
                filter: parsed.filter,
                column: parsed.column,
            });
        });

        return result;
    }

    private dlog(...args: any[]) {
        if (this._isDebug) {
            const prefix = this._debugTag ? `[debug:${this._debugTag}]` : '[debug]';
            console.log(prefix, ...args);
        }
    }

    constructor(rules: string, dataSource?: IDocumentDataSource, requestCache?: IFormulaRequestCache) {
        let formula = rules;
        this._dataSource = dataSource;
        this._requestCache = requestCache;
        this._isDebug = false;
        // Support: [debug] and [debug:tag]
        const debugMatch = /^\[debug(?::(?<tag>[^\]]+))?\]/i.exec(formula ?? '');
        if (debugMatch) {
            this._isDebug = true;
            if (debugMatch.groups?.tag) {
                this._debugTag = debugMatch.groups.tag.trim();
            }
            formula = formula.replace(debugMatch[0], '');
            this.dlog(formula);
        }
        this._rules = formula;
        this._config = ConfigService.get();
    }

    private cachedRemote<T>(operation: string, args: readonly unknown[], loader: () => Promise<T>): Promise<T> {
        return this._requestCache ? this._requestCache.getOrCreate(operation, args, loader) : loader();
    }

    Init(data?: Record<string, IFieldElem>, formMethods?: UseFormReturn<any>, parentRow?: any, resolveVariables = true) {
        this._preparedExpression = undefined;
        this._formMethods = formMethods;
        this._parentRow = parentRow;
        this._data = data;
        let m: RegExpExecArray | null;
        let f: IFormulaElem[] = [];
        let sourceRule = resolveVariables ? this.prepareVariables(this._rules, data) : this._rules;
        let formula = sourceRule;

        let i = 0;

        _formulaElems.forEach((fElem) => {
            while ((m = fElem.regex.exec(sourceRule)) !== null) {
                if (m.index === fElem.regex.lastIndex) {
                    fElem.regex.lastIndex++;
                }

                let match = m[0];

                if (
                    f.findIndex((m) => {
                        return m.match[0] === match;
                    }) === -1 &&
                    formula.includes(match)
                ) {
                    let watch = m.groups?.watch;
                    let fValue: IFieldElem | undefined;
                    if (fElem.type === FieldType.ParentField && data?.[`|Parent${watch}`]) {
                        fValue = data[`|Parent${watch}`];
                    } else if (fElem.type === FieldType.ParentField && this._parentRow) {
                        // достаем данные из родительской строки для данной подтаблицы
                        const parentFieldName = watch!;
                        fValue = {
                            index: 0,
                            name: parentFieldName,
                            value: this._parentRow[parentFieldName],
                        };
                    } else {
                        if (fElem.type == FieldType.SumColumn && watch) {
                            let parts = watch.split('||');
                            if (parts.length == 2) {
                                watch = parts[0];
                            }
                        }
                        fValue = data ? data[watch!] : undefined;
                    }

                    let elem = {
                        index: fValue?.index,
                        name: fValue ? fValue.name : watch!,
                        defValue: fValue?.value,
                        formulaPart: m.groups!,
                        match: m,
                        type: fElem.type,
                        params: {},
                    } as IFormulaElem;

                    if (fElem.type === FieldType.GetLinksCount || fElem.type === FieldType.GetAttachmentsCount) {
                        // Для функций без параметра watch добавляем фиктивный параметр, т.к. он обязательный
                        if (elem.match && elem.match.groups) {
                            elem.match.groups['watch'] = 'param';
                        }
                    }

                    let fieldVal = '{' + i + '}';
                    const source = new RegExp(escapeRegExp(match), 'g');
                    if (
                        fElem.type == FieldType.OffsetDay ||
                        fElem.type == FieldType.OffsetMonth ||
                        fElem.type == FieldType.OffsetWorkDay ||
                        fElem.type == FieldType.LastWorkDayAfterOffset ||
                        fElem.type == FieldType.OffsetYear ||
                        fElem.type == FieldType.SetDate ||
                        fElem.type == FieldType.SetTime ||
                        fElem.type == FieldType.GetDataFromSP
                    ) {
                        if (m && m.groups) {
                            [
                                'param',
                                'param1',
                                'param2',
                                'param3',
                                'param4',
                                'month',
                                'day',
                                'hour',
                                'minutes',
                                'offset',
                                'allowedWeekDays',
                            ].forEach((param) => {
                                if (m && m.groups) {
                                    if (m.groups[param] && isNaN(+m.groups[param])) {
                                        const fParam = data?.[m.groups[param]];
                                        if (fParam != null) {
                                            elem.params[m.groups[param]] =
                                                typeof fParam === 'object' ? fParam.name : fParam;
                                        }
                                    }
                                }
                            });
                        }
                    }
                    f.push(elem);
                    formula = formula?.replace(source, fieldVal!);
                    i++;
                }
            }
        });
        this._formula = formula;
        this._fields = f;
    }

    /**
     * Один проход: текст результата не становится новой ссылкой на переменную. Внутри строки
     * автора (`'variables([currentUserWorkGroups])'.split('|')`) — экранированное содержимое:
     * апостроф в названии группы не закрывает строку. Вне кавычек — текст как есть, как и
     * раньше: `variables([currentUserId])=={…}`, аргумент OFFSETDAY; отсутствующая переменная
     * даёт `undefined`, иначе `!(variables(X)=='1')` стало бы синтаксической ошибкой.
     */
    private prepareVariables(rules: string, data?: Record<string, IFieldElem>) {
        if (rules === null || rules === undefined || !rules.includes('variables(')) return rules;
        return substituteFormulaData(rules, VARIABLES_REFERENCE, (_match, [wrappedPath, plainPath], quote) => {
            const path = wrappedPath ?? plainPath;
            if (!path || !data?.variables) return { raw: '' };
            const value = path.split('.').reduce((current, key) => {
                const name = key.replace(/\[/g, '').replace(/\]/g, '');
                return current && Object.prototype.hasOwnProperty.call(Object(current), name) ? current[name] : undefined;
            }, data.variables.value as any);
            return quote ? { text: String(value) } : { raw: String(value) };
        });
    }

    GetFields() {
        let result: string[] = [];
        if (this._fields) {
            this._fields.forEach((f) => {
                let watch = f.match.groups?.watch!;
                if (f.type == FieldType.SumColumn && watch) {
                    let parts = watch.split('||');
                    if (parts.length == 2) {
                        watch = parts[0];
                    }
                }
                if (f.type == FieldType.ParentField) {
                    watch = '|Parent' + watch;
                }

                if (watch) {
                    result.push(watch);
                }
            });
            return result;
        }
        return [];
    }

    GetPreparedExpression() {
        return this._preparedExpression;
    }

    /**
     * Имена полей формулы ПОЗИЦИОННО, параллельно `_fields` (по одному на плейсхолдер `{i}`).
     * Порядок и длина обязаны совпадать с `_fields`: значения из useWatch подставляются в
     * `EvalFormulaValues`/`ReplaceFormulaValues` по индексу (`values[i] → _fields[i]`).
     * Поэтому здесь НЕЛЬЗЯ ни дедуплицировать, ни фильтровать, ни добавлять params —
     * иначе сдвиг индексов ломает вычисление формулы. Для набора зависимостей резолвера
     * см. {@link GetDependencyWatchFields}.
     */
    GetWatchFields() {
        return this._fields ? this._fields.map((field) => field.name) : [];
    }

    /**
     * Дедуплицированный набор имён полей-зависимостей формулы, включая field-backed параметры
     * удалённых функций (OFFSETWORKDAY/GETDATAFROMSP и т.п.). НЕ позиционный — предназначен для
     * построения графа зависимостей (validationIndex), а не для подстановки значений в формулу.
     */
    GetDependencyWatchFields() {
        if (this._fields) {
            const watches = new Set<string>();
            this._fields.forEach((field) => {
                if (field.name) watches.add(field.name);
                Object.values(field.params).forEach((name) => {
                    if (name) watches.add(name);
                });
            });
            return [...watches];
        }
        return [];
    }

    private isStandaloneUnconfiguredFact() {
        if (this._formula !== '{0}' || this._fields?.length !== 1) {
            return false;
        }

        const field = this._fields[0];
        if (field.type !== FieldType.Fact) {
            return false;
        }

        const tableKey = field.match.groups?.watch?.trim();
        const factName = field.match.groups?.name?.trim();
        if (!tableKey || !factName) {
            return true;
        }

        return !FormulaManager._factsConfigByTable.get(tableKey)?.has(factName);
    }

    async EvalFormulaValues(isEdit: boolean, isNew: boolean, values?: any[], strict = false) {
        if (this._formula) {
            let f: string | undefined = '';
            try {
                if (this.isStandaloneUnconfiguredFact()) {
                    return undefined;
                }

                f = await this.ReplaceFormulaValues(isEdit, isNew, values);
                let result;
                // Префиксный ! — JavaScript-отрицание; постфиксный ! остаётся факториалом mathjs.
                const expressionWithoutStrings = f?.replace(/'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/g, '""') ?? '';
                const hasLogicalNot = /(^|[([,:?=+\-*/%&|<>])\s*!/.test(expressionWithoutStrings);
                if (
                    hasLogicalNot ||
                    this._boolExp.some((x) => {
                        return f ? f.includes(x) : false;
                    })
                ) {
                    result = indirectEval(f!);
                } else {
                    const { evaluate } = await loadMathJs();
                    result = evaluate(f!);
                }
                if (this._isDebug) this.dlog(result);
                if (strict && typeof result === 'number' && !Number.isFinite(result)) {
                    throw new Error('Formula expression must produce a finite number.');
                }
                if (result === Infinity) {
                    return 0;
                }
                return result;
            } catch (error) {
                if (strict) throw error;
                console.log(this._rules);
                console.log(f);
                console.log(error);

                const debugSettingsStorageItem = window.localStorage.getItem('Debug-Settings');
                const debugSettings: IDebugSettings = debugSettingsStorageItem
                    ? JSON.parse(debugSettingsStorageItem)
                    : null;
                const notifyFormulaErrors =
                    this._config.environment !== 'PROD' && debugSettings?.notifyFormulaErrors === true;

                if (notifyFormulaErrors) {
                    sendNotification({
                        id: uuidv4(),
                        title: 'Ошибка вычисления формулы',
                        message: `<b>Ошибка:</b><div>${error}</div><b>Формула:</b><div>${this._rules}</div><b>Формула с подстановками:</b><div>${f}</div><br/><b>См. подробности в консоли браузера<b>`,
                        variant: 'red',
                    });
                }

                return;
            }
        }
        if (strict) throw new Error('Formula expression cannot be empty.');
        return true;
    }

    GetDefaultValues(isEdit: boolean, isNew: boolean, withoutQuotes?: boolean) {
        return this.getDefaultValues(isEdit, isNew, withoutQuotes);
    }

    async ReplaceFormulaValues(isEdit: boolean, isNew: boolean, values?: any[], withoutQuotes?: boolean) {
        if (this._isDebug) this.dlog(this._rules);

        if (!values?.length) {
            values = this.getDefaultValues(isEdit, isNew, withoutQuotes);
        } else if (this._fields) {
            // Внешние источники данных не зарегистрированы в react-hook-form и отсутствуют в watch —
            // подставляем их актуальные значения из карты полей (this._data).
            values = values.map((v, i) =>
                this._fields![i]?.name?.startsWith(EXTERNAL_DATA_SOURCE_PREFIX)
                    ? this.getDefaultValue(this._fields![i])
                    : v,
            );
        }

        if (!this._formula) {
            this._preparedExpression = this._formula;
            return this._formula;
        }

        let f = this.substitutes(isEdit, isNew, this._formula);
        this._preparedExpression = f;

        if (!values?.length) return f;
        if (withoutQuotes && values.every((v) => v == null)) {
            this._preparedExpression = '';
            return '';
        }

        // получаем значения
        const fieldVals = await Promise.all(values.map((_, i) => this.getFieldsValue(values!, i, withoutQuotes)));

        // заменяем за один проход
        f = f.replace(/\{(\d+)\}/g, (_m, idxStr) => {
            const idx = idxStr | 0;
            const v = fieldVals[idx];
            return v == null ? '' : String(v);
        });

        if (withoutQuotes) f = f.replace(/\|/g, ',');

        this._preparedExpression = f;
        if (this._isDebug) this.dlog(f);
        return f;
    }

    private formatFieldValueByType(field: any) {
        if (field === undefined || field === null) {
            return 'null';
        }

        if (isIsoDateString(field)) {
            let val = new Date(field); // Moment(value).toDate();
            return val.getTime().toString();
        } else {
            switch (typeof field) {
                case 'string':
                    return formulaStringLiteral(field);
                case 'number':
                    return '' + field + '';
                case 'object':
                    if (field instanceof Date) {
                        return field.getTime().toString();
                    } else {
                        return '' + field + '';
                    }

                    break;
                default:
                    return '' + field + '';
            }
        }
    }
    private formatDisplayFieldValue(field: any) {
        if (field === undefined || field === null) {
            return '';
        } else {
            return field;
        }
    }

    private getDefaultValue(field: IFormulaElem) {
        if (
            field.name?.startsWith(EXTERNAL_DATA_SOURCE_PREFIX) &&
            this._data &&
            Object.prototype.hasOwnProperty.call(this._data, field.name)
        ) {
            return this._data[field.name]?.value;
        }

        return field.defValue;
    }

    private getDefaultValues(isEdit: boolean, isNew: boolean, withoutQuotes?: boolean) {
        let values: any[] = [];
        if (this._fields && this._formula) {
            if ((isEdit || isNew) && this._formMethods) {
                this._fields.forEach((f) => {
                    if (f.name?.startsWith(EXTERNAL_DATA_SOURCE_PREFIX)) {
                        const value = this.getDefaultValue(f);
                        if (withoutQuotes && value === null) {
                            values.push('');
                        } else {
                            values.push(value);
                        }
                        return;
                    }
                    if (
                        f.type == FieldType.SetGuid ||
                        f.type == FieldType.ParentField ||
                        f.type == FieldType.SetDate ||
                        f.type == FieldType.Now ||
                        f.type == FieldType.CurrentYear ||
                        f.type == FieldType.StrToDate ||
                        f.type == FieldType.NowOnlyDate
                    ) {
                        if (withoutQuotes) {
                            values.push('null');
                        } else {
                            values.push(null);
                        }
                    } else {
                        values.push(this._formMethods?.getValues(f.name));

                        //Если есть еще один параметр у формулы
                        if (f.formulaPart.param) {
                            let flds = this._formMethods?.getValues()?.fields as any[];
                            if (flds) {
                                let paramVal = flds.find((k) => k.name == f.formulaPart.param)?.value;
                                if (paramVal !== undefined) values.push(paramVal);
                            }
                        }
                    }
                });
            } else {
                this._fields.forEach((f) => {
                    const value = this.getDefaultValue(f);
                    if (withoutQuotes) {
                        if (value === null) {
                            values.push('');
                        } else {
                            values.push(value as number);
                        }
                    } else {
                        values.push(value as number);
                    }
                });
            }
        }
        return values;
    }

    private async getFieldsValue(values: any[], index: number, withoutQuotes?: boolean) {
        let result;
        if (values && this._formula && this._fields) {
            let formulaElm = this._fields[index];
            if (formulaElm) {
                switch (formulaElm.type) {
                    case FieldType.SumColumn:
                        result = await this.sumColumn(values, formulaElm, index);
                        break;
                    case FieldType.DictExist:
                        result = await this.dictExist(values, formulaElm, index);
                        break;
                    case FieldType.GetDataFromSP:
                        result = await this.getDataFromSP(values, formulaElm, index);
                        break;
                    case FieldType.Count:
                        result = this.count(values, formulaElm, index);
                        break;
                    case FieldType.Fact:
                        result = await this.getFact(values, formulaElm, index);
                        break;
                    case FieldType.Length:
                        result = this.length(values, formulaElm, index);
                        break;
                    case FieldType.GetYear:
                        result = this.getYear(values, formulaElm, index);
                        break;
                    case FieldType.SetDate:
                        result = this.setDate(values, formulaElm, index);
                        break;
                    case FieldType.SetTime:
                        result = this.setTime(values, formulaElm, index);
                        break;
                    case FieldType.GetMonth:
                        result = this.getMonth(values, formulaElm, index);
                        break;
                    case FieldType.GetDay:
                        result = this.getDay(values, formulaElm, index);
                        break;
                    case FieldType.OffsetYear:
                        result = this.offsetYear(values, formulaElm, index);
                        break;
                    case FieldType.FormatStr:
                        result = this.formatStr(values, formulaElm, index);
                        break;
                    case FieldType.OffsetMonth:
                        result = this.offsetMonth(values, formulaElm, index);
                        break;
                    case FieldType.OffsetDay:
                        result = this.offsetDay(values, formulaElm, index);
                        break;
                    case FieldType.OffsetWorkDay:
                        result = await this.offsetWorkDay(values, formulaElm, index);
                        break;
                    case FieldType.LastWorkDayAfterOffset:
                        result = await this.getLastWorkDayAfterOffset(values, formulaElm, index);
                        break;
                    case FieldType.CheckRow:
                        result = await this.checkRow(values, formulaElm, index);
                        break;
                    case FieldType.SetGuid:
                        result = uuidv4();
                        break;
                    case FieldType.Now: {
                        let defVal = new Date();
                        result = new Date(
                            defVal.getFullYear(),
                            defVal.getMonth(),
                            defVal.getDate(),
                            defVal.getHours(),
                            defVal.getMinutes(),
                            0,
                            0,
                        ).toISOString();
                        break;
                    }
                    case FieldType.CurrentYear:
                        result = new Date().getFullYear();
                        break;
                    case FieldType.StrToDate:
                        result = this.strToDate(values, formulaElm, index);
                        break;
                    case FieldType.NowOnlyDate:
                        {
                            let defVal = new Date();
                            result = new Date(
                                defVal.getFullYear(),
                                defVal.getMonth(),
                                defVal.getDate(),
                                0,
                                0,
                                0,
                                0,
                            ).toISOString();
                        }
                        break;
                    case FieldType.ParentField:
                        result = this._data?.[`|Parent${formulaElm.match.groups!.watch}`]
                            ? this._formMethods
                                ? this._formMethods.getValues(formulaElm.name)
                                : formulaElm.defValue
                            : (this._parentRow?.[formulaElm.match.groups!.watch] ?? '');
                        break;
                    case FieldType.SplitAndGetByIndex:
                        result = await this.splitAndGetByIndex(values, formulaElm, index);
                        break;
                    case FieldType.SplitAndGetCount:
                        result = await this.splitAndGetCount(values, formulaElm, index);
                        break;
                    case FieldType.GetTableCell:
                        result = await this.getTableCell(values, formulaElm, index);
                        break;
                    case FieldType.GetTableCells:
                        result = await this.getTableCells(values, formulaElm, index);
                        break;
                    case FieldType.GetTableMaxCell:
                        result = await this.getTableMaxCell(values, formulaElm, index);
                        break;
                    case FieldType.GetTableMinCell:
                        result = await this.getTableMinCell(values, formulaElm, index);
                        break;
                    case FieldType.GetLinksCount:
                        result = await this.getLinksCount(values, formulaElm, index);
                        break;
                    case FieldType.GetAttachmentsCount:
                        result = await this.getAttachmentsCount(values, formulaElm, index);
                        break;
                    case FieldType.EscapeString:
                        result = this.escapeString(values, formulaElm, index);
                        break;
                    default:
                        result = values[index];
                        break;
                }
            }
        }
        return withoutQuotes == undefined || withoutQuotes == false
            ? this.formatFieldValueByType(result)
            : this.formatDisplayFieldValue(result);
    }
    private static isDocLevelField(field: string) {
        return field.includes('|Document') || field.startsWith('#links') || field === 'variables';
    }

    private static getDocFieldValue(
        field: string,
        data?: Record<string, IFieldElem>,
        formMethods?: UseFormReturn<any>,
    ): any {
        if (!data) return undefined;

        const fieldData = data[field];
        if (!fieldData) return undefined;

        if (formMethods && fieldData?.name) {
            const formValue = formMethods.getValues(fieldData.name);
            if (formValue !== undefined) {
                return formValue;
            }
        }

        return fieldData.value;
    }

    private static async evalRowsByFilter(
        rows: any[] | undefined | null,
        filter: string | undefined,
        mode: FactMode,
        sumColumn: string | undefined,
        data?: Record<string, IFieldElem>,
        formMethods?: UseFormReturn<any>,
    ) {
        const safeRows = Array.isArray(rows) ? rows : [];
        const getNumber = (value: any) => {
            if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
            if (typeof value === 'string') {
                const normalized = value.replace(',', '.');
                const parsed = Number(normalized);
                return Number.isFinite(parsed) ? parsed : 0;
            }
            if (typeof value === 'boolean') return value ? 1 : 0;
            return 0;
        };
        let sum = 0;

        if (!filter) {
            if (mode === 'count') return safeRows.length;
            if (mode === 'sum') {
                if (!sumColumn) return 0;
                return safeRows.reduce((acc, row) => acc + getNumber(row?.[sumColumn]), 0);
            }
            if (mode === 'all') return true;
            return safeRows.length > 0;
        }

        const filterMng = new FormulaManager(filter);
        filterMng.Init(data, formMethods);

        const fields = filterMng.GetFields();
        const valuesFilter = new Array(fields.length);
        const docValues = new Array(fields.length);
        const isDocField = new Array<boolean>(fields.length);

        for (let j = 0; j < fields.length; j++) {
            const field = fields[j];
            if (FormulaManager.isDocLevelField(field)) {
                isDocField[j] = true;
                docValues[j] = FormulaManager.getDocFieldValue(field, data, formMethods);
            } else {
                isDocField[j] = false;
            }
        }

        let matched = 0;
        for (let i = 0; i < safeRows.length; i++) {
            const row = safeRows[i];

            for (let j = 0; j < fields.length; j++) {
                valuesFilter[j] = isDocField[j] ? docValues[j] : row?.[fields[j]];
            }

            const checkResult = await filterMng.EvalFormulaValues(false, false, valuesFilter);
            const isTrue = !!checkResult;

            if (mode === 'count') {
                if (isTrue) matched++;
                continue;
            }
            if (mode === 'sum') {
                if (isTrue && sumColumn) {
                    sum += getNumber(row?.[sumColumn]);
                }
                continue;
            }

            if (mode === 'any' && isTrue) return true;
            if (mode === 'all' && !isTrue) return false;
        }

        if (mode === 'count') return matched;
        if (mode === 'sum') return sum;
        if (mode === 'all') return true;
        return false;
    }

    private static async evalConfiguredFact(
        tableKey: string,
        factName: string,
        rows: any[] | undefined | null,
        data?: Record<string, IFieldElem>,
        formMethods?: UseFormReturn<any>,
    ) {
        const tableFacts = FormulaManager._factsConfigByTable.get(tableKey);
        const factDefinition = tableFacts?.get(factName);
        if (!factDefinition) return undefined;

        const rowsObject = Array.isArray(rows) ? (rows as object) : null;
        if (rowsObject) {
            let tableCache = FormulaManager._factsCacheByTable.get(tableKey);
            if (!tableCache) {
                tableCache = new WeakMap();
                FormulaManager._factsCacheByTable.set(tableKey, tableCache);
            }

            let factCache = tableCache.get(rowsObject);
            if (!factCache) {
                factCache = new Map();
                tableCache.set(rowsObject, factCache);
            }

            if (factCache.has(factName)) {
                return factCache.get(factName);
            }

            const factValue = await FormulaManager.evalRowsByFilter(
                rows,
                factDefinition.filter,
                factDefinition.mode,
                factDefinition.column,
                data,
                formMethods,
            );
            factCache.set(factName, factValue);
            return factValue;
        }

        return FormulaManager.evalRowsByFilter(
            rows,
            factDefinition.filter,
            factDefinition.mode,
            factDefinition.column,
            data,
            formMethods,
        );
    }

    private async getFact(values: any[], formulaElm: IFormulaElem, index: number) {
        const tableKey = formulaElm.match.groups?.watch?.trim();
        const factName = formulaElm.match.groups?.name?.trim();
        if (!tableKey || !factName) return undefined;

        const rows = values[index] as any[] | undefined | null;
        return FormulaManager.evalConfiguredFact(tableKey, factName, rows, this._data, this._formMethods);
    }

    private async checkRow(values: any[], formulaElm: IFormulaElem, index: number) {
        const rows = values[index] as any[] | undefined | null;
        if (!rows) return false;

        const type = formulaElm.match.groups?.param?.trim();
        const mode: FactMode = type === 'all' ? 'all' : 'any';
        const filter = formulaElm.match.groups?.filter;

        const result = await FormulaManager.evalRowsByFilter(
            rows,
            filter,
            mode,
            undefined,
            this._data,
            this._formMethods,
        );
        return !!result;
    }
    private count(values: any[], _formulaElm: IFormulaElem, index: number) {
        let rows = values[index] as any[];
        if (rows === undefined || rows === null) {
            return 0;
        }
        return rows.length;
    }

    private length(values: any[], _formulaElm: IFormulaElem, index: number) {
        let value = values[index] as string;
        if (value === undefined || value === null) {
            return 0;
        }
        return value.length;
    }

    private getYear(values: any[], _formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        return defVal.getFullYear();
    }
    private setDate(values: any[], formulaElm: IFormulaElem, index: number) {
        let _year =
            values[index] != null && !isNaN(+values[index])
                ? +values[index]
                : this.getOffsetVal(formulaElm.name, formulaElm);
        let _month = this.getOffsetVal(formulaElm.match.groups?.month, formulaElm);
        let _day = this.getOffsetVal(formulaElm.match.groups?.day, formulaElm);
        let _hour = this.getOffsetVal(formulaElm.match.groups?.hour, formulaElm);
        let _minutes = this.getOffsetVal(formulaElm.match.groups?.minutes, formulaElm);

        let date = new Date(_year, _month - 1, _day, _hour, _minutes, 0, 0);
        if (isNaN(date.getTime())) return '';
        return date.toISOString();
    }
    private setTime(values: any[], formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        if (isNaN(defVal.getTime())) return '';
        let _hour = this.getOffsetVal(formulaElm.match.groups?.hour, formulaElm);
        let _minutes = this.getOffsetVal(formulaElm.match.groups?.minutes, formulaElm);

        let result = new Date(defVal.getFullYear(), defVal.getMonth(), defVal.getDate(), _hour, _minutes, 0, 0);
        return result.toISOString();
    }
    private getDay(values: any[], _formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        return defVal.getDate();
    }
    private getMonth(values: any[], _formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        return defVal.getMonth() + 1;
    }
    private offsetMonth(values: any[], formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        let offset = this.getOffsetVal(formulaElm.match.groups?.param, formulaElm);
        let result = new Date(
            defVal.getFullYear(),
            defVal.getMonth() + offset,
            defVal.getDate(),
            defVal.getHours(),
            defVal.getMinutes(),
            defVal.getSeconds(),
            defVal.getMilliseconds(),
        );
        return result.toISOString();
    }
    private offsetDay(values: any[], formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        let offset = this.getOffsetVal(formulaElm.match.groups?.param, formulaElm);
        let offsetConst = this.getOffsetVal(formulaElm.match.groups?.param1, formulaElm);
        if (offsetConst) {
            offset = offset + offsetConst;
        }
        let result = new Date(
            defVal.getFullYear(),
            defVal.getMonth(),
            defVal.getDate() + offset,
            defVal.getHours(),
            defVal.getMinutes(),
            defVal.getSeconds(),
            defVal.getMilliseconds(),
        );
        return result.toISOString();
    }

    private strToDate(_values: any[], formulaElm: IFormulaElem, _index: number) {
        let defVal = formulaElm.match.groups?.strdate;
        // Общий RU-парсер (1–2 цифры дня/месяца) вместо инлайна. На невалиде бросаем —
        // формула с битой датой должна падать явно, как раньше (date-fns parse давал
        // Invalid Date → .toISOString() кидал RangeError).
        const result = parseRuDate(defVal!);
        if (!result) throw new RangeError(`strToDate: невалидная RU-дата "${defVal}"`);
        return result.toISOString();
    }

    private getOffsetWorkDayDate(value: unknown, formulaElm: IFormulaElem) {
        const firstArgument = formulaElm.match.groups?.watch?.trim();
        const now = new Date();
        const defVal =
            firstArgument === '{now}' || firstArgument === '{nowOnlyDate}'
                ? new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate(),
                      firstArgument === '{now}' ? now.getHours() : 0,
                      firstArgument === '{now}' ? now.getMinutes() : 0,
                      0,
                      0,
                  )
                : value instanceof Date
                  ? new Date(value.getTime())
                  : typeof value === 'number'
                    ? new Date(value)
                    : typeof value === 'string'
                      ? new Date(value)
                      : new Date(Number.NaN);

        if (Number.isNaN(defVal.getTime())) {
            const argumentLabel = firstArgument ?? String(value);
            throw new Error(`OFFSETWORKDAY: невалидная дата в первом аргументе "${argumentLabel}".`);
        }

        return defVal;
    }

    private async offsetWorkDay(values: any[], formulaElm: IFormulaElem, index: number) {
        const defVal = this.getOffsetWorkDayDate(values[index], formulaElm);
        let offset = this.getOffsetVal(formulaElm.match.groups?.param, formulaElm);
        let offsetConst = this.getOffsetVal(formulaElm.match.groups?.param1, formulaElm);
        if (offsetConst) {
            offset = offset + offsetConst;
        }
        const result = await this.cachedRemote('OFFSETWORKDAY', [defVal.getTime(), offset], async () => {
            if (this._dataSource) {
                if (
                    !this._dataSource.supportedFormulaFunctions.has('OFFSETWORKDAY') ||
                    !this._dataSource.offsetWorkDay
                ) {
                    throw new Error('OFFSETWORKDAY не поддерживается текущим источником данных документа.');
                }
                return this._dataSource.offsetWorkDay(defVal, offset);
            }
            return (await EnvironmentService.offsetWorkDay(defVal, offset)).data;
        });
        return new Date(result).toISOString();
    }

    private async getLastWorkDayAfterOffset(values: any[], formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        let offset = this.getOffsetVal(formulaElm.match.groups?.offset, formulaElm);

        let allowedWeekDays: number[];
        try {
            allowedWeekDays = formulaElm.match.groups?.allowedWeekDays
                ? JSON.parse(formulaElm.match.groups?.allowedWeekDays).filter((item: any) => typeof item === 'number')
                : [];
        } catch {
            allowedWeekDays = [];
        }

        const result = await this.cachedRemote(
            'LASTWORKDAYAFTEROFFSET',
            [defVal.getTime(), offset, allowedWeekDays],
            async () => {
                if (this._dataSource) {
                    if (
                        !this._dataSource.supportedFormulaFunctions.has('LASTWORKDAYAFTEROFFSET') ||
                        !this._dataSource.lastWorkDayAfterOffset
                    ) {
                        throw new Error(
                            'LASTWORKDAYAFTEROFFSET не поддерживается текущим источником данных документа.',
                        );
                    }
                    return this._dataSource.lastWorkDayAfterOffset(defVal, offset, allowedWeekDays);
                }
                return (await EnvironmentService.lastWorkDayAfterOffset(defVal, offset, allowedWeekDays)).data;
            },
        );
        return new Date(result).toISOString();
    }

    private async dictExist(values: any[], formulaElm: IFormulaElem, index: number) {
        let code = values[index];
        let dictName = formulaElm.match.groups?.name!;
        let dictColumn = formulaElm.match.groups?.column!;
        let returnColumn = formulaElm.match.groups?.return;
        let type = formulaElm.match.groups?.type ? formulaElm.match.groups?.type : 'eq';
        if (this._isDebug) {
            this.dlog(dictName + ';;' + dictColumn + ';;' + code + ';;' + returnColumn + ';;' + type);
        }
        const dictionaryItem = await this.cachedRemote('DICTEXIST', [dictName, dictColumn, code, type], async () => {
            if (this._dataSource) {
                if (
                    !this._dataSource.supportedFormulaFunctions.has('DICTEXIST') ||
                    !this._dataSource.getDictionaryItem
                ) {
                    throw new Error('DICTEXIST не поддерживается текущим источником данных документа.');
                }
                return this._dataSource.getDictionaryItem(dictName, dictColumn, code, type);
            }
            return (await DictionariesService.getItemByFilter(dictName, dictColumn, code, type)).data;
        });

        if (dictionaryItem) {
            if (returnColumn == 'code') {
                return dictionaryItem.code;
            } else {
                for (let index = 0; index < dictionaryItem.fields.length; index++) {
                    const col = dictionaryItem.fields[index];
                    if (col.name == returnColumn && col.value) {
                        return col.value.toString();
                    }
                }
            }
        }

        return '-1';
    }

    private getXmlTable(array: any[]) {
        const result: any[] = [];
        for (let index = 0; index < array.length; index++) {
            const element = array[index];
            const row = {} as Record<string, any>;
            const keys = Object.keys(element);
            for (let indexKey = 0; indexKey < keys.length; indexKey++) {
                const key = keys[indexKey];
                let formatedKey: string = key.startsWith('|') ? key.substring(1) : key;
                formatedKey = formatedKey.replace('|', '_');
                let cell = element[key];
                if (Array.isArray(cell)) {
                    row[formatedKey] = this.getXmlTable(cell as any[]);
                } else {
                    row[formatedKey] = cell;
                }
            }
            result.push(row);
        }
        const rows = {} as Record<string, any>;
        rows['Row'] = result;
        const returnData = {} as Record<string, any>;
        returnData['Rows'] = rows;
        return returnData;
    }

    private async getDataFromSP(values: any[], formulaElm: IFormulaElem, index: number) {
        let spName = formulaElm.match.groups?.name!;
        let column = formulaElm.match.groups?.param!;
        let returnParam = undefined;
        let params = {} as Record<string, any>;
        let tmpParams: any[] = [];
        let indexParams = 0;
        let totalVal = index;
        if (column && column.indexOf('|Document') > -1) {
            totalVal++;
        }

        for (let i = index; i <= totalVal; i++) {
            const param = values[i];
            if (Array.isArray(param)) {
                let result: string;
                if (column == 'XML') {
                    const data = this.getXmlTable(param);
                    result = new x2js().js2xml(data);
                } else {
                    result = param.map((a) => a[column]).join(',');
                }
                params['param' + i] = result;
                indexParams++;
            } else {
                params['param' + i] = param;
                indexParams++;
            }
        }

        ['param1', 'param2', 'param3', 'param4'].forEach((param) => {
            if (formulaElm.match && formulaElm.match.groups) {
                if (formulaElm.match.groups[param]) {
                    let fParam = formulaElm.match.groups[param];
                    if (fParam) {
                        if (formulaElm.params[fParam]) {
                            let valField = this._formMethods?.getValues(formulaElm.params[fParam]);
                            if (valField) {
                                tmpParams.push(valField);
                            } else {
                                tmpParams.push(formulaElm.params[fParam]);
                            }
                        } else {
                            tmpParams.push(fParam);
                        }
                    }
                }
            }
        });
        if (tmpParams.length > 0) {
            returnParam = tmpParams.pop();
        }
        for (let index = 0; index < tmpParams.length; index++) {
            params['param' + indexParams] = tmpParams[index];
            indexParams++;
        }

        const rows = await this.cachedRemote('GETDATAFROMSP', [spName, params], async () => {
            if (this._dataSource) {
                if (
                    !this._dataSource.supportedFormulaFunctions.has('GETDATAFROMSP') ||
                    !this._dataSource.getStoredProcedureData
                ) {
                    throw new Error('GETDATAFROMSP не поддерживается текущим источником данных документа.');
                }
                return this._dataSource.getStoredProcedureData(spName, params);
            }
            return (await EnvironmentService.getExtendData(spName, JSON.stringify(params))).data;
        });

        if (rows.length > 0) {
            let res = rows[0];
            if (returnParam !== undefined && Object.keys(res).includes(returnParam)) {
                return res[returnParam];
            } else if (Object.keys(res).includes(spName)) {
                return res[spName];
            } else {
                return undefined;
            }
        }

        return '';
    }

    private async getLinksCount(_values: any[], formulaElm: IFormulaElem, _index: number) {
        let tableKey = formulaElm.match.groups?.tableKey!;
        let docId = this._data ? ((this._data['doc_Id']?.value as string) ?? null) : null;
        return this.cachedRemote('GETLINKSCOUNT', [docId, tableKey], async () => {
            if (this._dataSource) {
                if (!this._dataSource.supportedFormulaFunctions.has('GETLINKSCOUNT')) {
                    throw new Error('GETLINKSCOUNT не поддерживается текущим источником данных документа.');
                }
                return this._dataSource.getLinksCount(tableKey);
            }

            if (!docId) return 0;
            const linksSvc = new DocumentLinksService(docId);

            let cacheKey = `${docId}_${tableKey}`;
            let cntCached = FormulaManager._linkCountCache[cacheKey];
            if (cntCached) {
                let res = await cntCached;
                return res?.data ?? 0;
            }

            let fetchCountFunc = linksSvc.fetchCount(tableKey);
            FormulaManager._linkCountCache[cacheKey] = fetchCountFunc;
            setTimeout(() => {
                FormulaManager._linkCountCache[cacheKey] = undefined;
            }, FormulaManager._cacheDurationMs);
            let response = await fetchCountFunc;
            return response?.data ?? 0;
        });
    }

    private async getAttachmentsCount(_values: any[], formulaElm: IFormulaElem, _index: number) {
        let tableKey = formulaElm.match.groups?.tableKey!;
        let docId = this._data ? ((this._data['doc_Id']?.value as string) ?? null) : null;
        return this.cachedRemote('GETATTACHMENTSCOUNT', [docId, tableKey], async () => {
            if (this._dataSource) {
                if (!this._dataSource.supportedFormulaFunctions.has('GETATTACHMENTSCOUNT')) {
                    throw new Error('GETATTACHMENTSCOUNT не поддерживается текущим источником данных документа.');
                }
                return this._dataSource.getAttachmentsCount(tableKey);
            }

            if (!docId) return 0;
            const attachService = new DocumentAttachService(docId);
            let response = await attachService.fetchCount(tableKey);
            return response?.data ?? 0;
        });
    }

    private offsetYear(values: any[], formulaElm: IFormulaElem, index: number) {
        let defVal = new Date(values[index]);
        let offset = this.getOffsetVal(formulaElm.match.groups?.param, formulaElm);
        let result = new Date(
            defVal.getFullYear() + offset,
            defVal.getMonth(),
            defVal.getDate(),
            defVal.getHours(),
            defVal.getMinutes(),
            defVal.getSeconds(),
            defVal.getMilliseconds(),
        );
        return result.toISOString();
    }

    private formatStr(values: any[], formulaElm: IFormulaElem, index: number) {
        let fieldVal = values[index];
        let type = formulaElm.match.groups?.param!;
        let result = '';
        if (fieldVal !== undefined && fieldVal !== null) {
            switch (type) {
                case 'money':
                    result = (+fieldVal)?.toLocaleString('ru-Ru', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    });

                    break;
                case 'date':
                    result = safeFormatDate(fieldVal, 'DD.MM.YYYY');
                    break;
                case 'datetime':
                    result = safeFormatDate(fieldVal, 'DD.MM.YYYY HH:mm');
                    break;

                default:
                    result = fieldVal.toString();
                    break;
            }
        }
        return result;
    }

    private escapeString(values: any[], _formulaElm: IFormulaElem, index: number) {
        let fieldVal = values[index];
        // экранируем следующие символы: ' " ` \
        return fieldVal?.toString().replace(/['"`\\]/g, '\\$&') ?? '';
    }

    private getOffsetVal(input: any, formulaElm: IFormulaElem) {
        if (input) {
            if (!isNaN(+input)) {
                return +input;
            } else {
                let key = input.indexOf('fields.[') > -1 ? input : formulaElm.params[input];
                if (key != null) {
                    let valField = this._formMethods?.getValues(key);
                    if (valField != null && !isNaN(+valField)) {
                        return +valField;
                    }
                }
            }
        }
        return 0;
    }

    private static _mathInstance: any = null;

    private static async getMath() {
        if (!FormulaManager._mathInstance) {
            const { all, create } = await loadMathJs();
            FormulaManager._mathInstance = create(all, {
                number: 'BigNumber',
                precision: 64,
                epsilon: 1e-60,
            });
        }
        return FormulaManager._mathInstance;
    }

    private async sumColumn(values: any[], formulaElm: IFormulaElem, index: number, checkSubtable: boolean = true) {
        const rows = values[index] as any[] | undefined | null;
        let sumResult = 0;
        let subTable: string | undefined;

        const math = await FormulaManager.getMath();

        if (checkSubtable) {
            const watch = formulaElm.match.groups?.watch;
            const parts = watch?.split('||');
            if (parts && parts.length === 2) {
                subTable = '|' + parts[1];
            }
        }

        if (!rows?.length) return sumResult;

        const filter = checkSubtable ? formulaElm.match.groups?.filter : undefined;

        let filterMng: FormulaManager | null = null;
        let coll: string[] = [];
        let filterValues: any[] = [];

        if (filter) {
            filterMng = new FormulaManager(filter);
            filterMng.Init();
            coll = filterMng.GetFields();
            filterValues = new Array(coll.length);
        }

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];

            let checkResult = true;
            if (filterMng) {
                for (let j = 0; j < coll.length; j++) {
                    filterValues[j] = row?.[coll[j]];
                }
                checkResult = await filterMng.EvalFormulaValues(false, false, filterValues);
            }

            if (!checkResult) continue;

            type ObjectKey = keyof typeof row;
            let val: any;

            if (subTable === undefined) {
                const attrName = formulaElm.match.groups?.param as ObjectKey;
                val = row?.[attrName];
            } else {
                const attrName = subTable as ObjectKey;
                val = +(await this.sumColumn([row?.[attrName]], formulaElm, 0, false));
            }

            if (val === undefined || val === null) continue;

            const n = +val;
            sumResult = (math as any).number(
                (math as any).sum((math as any).bignumber(sumResult), (math as any).bignumber(n)),
            );
        }

        return sumResult;
    }

    private IS_EDIT_REGEX = /\{isEdit\}/g;
    private IS_NEW_REGEX = /\{isNew\}/g;
    private IS_VIEW_REGEX = /\{isView\}/g;

    /** Без флага g: у глобальной регулярки test() ведёт lastIndex и через раз возвращает false. */
    private static readonly MODE_TOKENS_REGEX = /\{is(Edit|New|View)\}/;

    /** Типы токенов, значение которых зависит от режима: в edit/new getDefaultValues подставляет
     *  им null, а в просмотре — снимок значения из карты полей. */
    private static readonly MODE_DEPENDENT_TYPES: FieldType[] = [
        FieldType.SetGuid,
        FieldType.ParentField,
        FieldType.SetDate,
        FieldType.Now,
        FieldType.CurrentYear,
        FieldType.StrToDate,
        FieldType.NowOnlyDate,
    ];

    /**
     * Зависит ли результат правила от режима карточки (view↔edit). Признак для селективного
     * пересчёта формул на смене режима — единственный источник правды о том, что делает формулу
     * режимно-зависимой (потребители: useFormulaState, useDocumentVisibleTabs).
     *
     * Зависимость дают два разных механизма, и оба видны только по разбору формулы:
     *  - токены {isEdit}/{isNew}/{isView} — их подставляет режим (substitutes);
     *  - токены, которым в edit/new подставляется null, а в просмотре — снимок значения из карты
     *    полей ({Now}, {SetGuid}, {ParentField} и пр., см. getDefaultValues): результат меняется
     *    от режима и без is-токенов.
     *
     * Смена самого источника значений (в edit/new — живая форма, в просмотре — снимок карты) здесь
     * не учитывается: в просмотре писать в форму мимо карты некому — стартовые каскады в нём не
     * идут (runInvokeChangesInit), а строки таблиц-линков вливаются в форму и карту одним патчем.
     */
    public DependsOnMode(): boolean {
        if (this._rules && FormulaManager.MODE_TOKENS_REGEX.test(this._rules)) return true;
        return !!this._fields?.some((field) => FormulaManager.MODE_DEPENDENT_TYPES.includes(field.type));
    }

    private substitutes(isEdit: boolean, isNew: boolean, val: string) {
        val = val.replace(this.IS_EDIT_REGEX, isEdit ? 'true' : 'false');
        val = val.replace(this.IS_NEW_REGEX, isNew ? 'true' : 'false');
        val = val.replace(this.IS_VIEW_REGEX, !isEdit && !isNew ? 'true' : 'false');

        //val = val?.toString().replace(/\{isEdit\}/g, isEdit ? 'true' : 'false');
        //val = val?.toString().replace(/\{isNew\}/g, isNew ? 'true' : 'false');
        //val = val?.toString().replace(/\{isView\}/g, !isEdit && !isNew ? 'true' : 'false');

        return val;
    }

    private async splitAndGetByIndex(values: any[], formulaElm: IFormulaElem, index: number) {
        let value = values[index] as string;
        let prm_separator = formulaElm.match.groups?.separator as string;
        let prm_index = parseInt(formulaElm.match.groups?.index!);
        let prm_skipEmpty = formulaElm.match.groups?.skipEmpty === 'true';
        let data = value.split(prm_separator);
        if (prm_skipEmpty) {
            data = data.filter((x) => x);
        }
        if (data.length == 0) return undefined;
        return data[prm_index];
    }

    private async splitAndGetCount(values: any[], formulaElm: IFormulaElem, index: number) {
        let value = values[index] as string;
        let prm_separator = formulaElm.match.groups?.separator as string;
        let prm_skipEmpty = formulaElm.match.groups?.skipEmpty === 'true';
        let data = value.split(prm_separator);
        if (prm_skipEmpty) {
            data = data.filter((x) => x);
        }
        return data.length;
    }

    private async getTableCell(values: any[], formulaElm: IFormulaElem, index: number) {
        const value = values[index] as any[];
        if (value == null) return null;
        const columnName = formulaElm.match.groups?.columnName as string;
        const rowIndex = parseInt(formulaElm.match.groups?.rowIndex as string);

        return value?.[rowIndex]?.[columnName];
    }

    private async getTableCells(values: any[], formulaElm: IFormulaElem, index: number) {
        let value = values[index] as any[];
        if (value == null) return null;
        let columnName = formulaElm.match.groups?.columnName as string;
        let data = value.map((row) => row[columnName]);

        let sep = formulaElm.match.groups?.separator as string;
        sep = sep ?? '|';

        return data.join(sep);
    }

    private async getTableMaxCell(values: any[], formulaElm: IFormulaElem, index: number) {
        const value = values[index] as any[];
        if (!Array.isArray(value) || value.length === 0) return null;

        const columnName = formulaElm.match?.groups?.columnName as string;
        const data = value
            .map((row) => (columnName ? row?.[columnName] : row))
            .filter((item) => item !== null && item !== undefined && item !== '');

        if (data.length === 0) return null;

        switch (typeof data[0]) {
            case 'number':
                return Math.max(...(data as number[]));

            case 'string': {
                const strs = data as unknown[];

                // берём только строки
                const onlyStrings = strs.filter((x): x is string => typeof x === 'string');

                // парсим только если все элементы ISO-даты
                if (onlyStrings.length === data.length && onlyStrings.every(isISODate)) {
                    const times = onlyStrings.map((s) => Date.parse(s));
                    return new Date(Math.max(...times));
                }

                const maxStr = onlyStrings.reduce<string | null>(
                    (acc, cur) => (acc === null || cur > acc ? cur : acc),
                    null,
                );
                return maxStr;
            }
            case 'object':
                if (data[0] instanceof Date) {
                    const times = (data as Date[]).map((d) => d.getTime());
                    return new Date(Math.max(...times));
                } else {
                    return Math.max(...(data as any[]));
                }

            default:
                return data[0];
        }
    }

    private async getTableMinCell(values: any[], formulaElm: IFormulaElem, index: number) {
        const value = values[index] as any[];
        if (!Array.isArray(value) || value.length === 0) return null;

        const columnName = formulaElm.match?.groups?.columnName as string;
        const data = value
            .map((row) => (columnName ? row?.[columnName] : row))
            .filter((item) => item !== null && item !== undefined && item !== '');

        if (data.length === 0) return null;

        switch (typeof data[0]) {
            case 'number':
                return Math.min(...(data as number[]));

            case 'string': {
                const items = (data as unknown[]).filter((x): x is string => typeof x === 'string');

                // парсим только если все элементы ISO-строки
                if (items.length === data.length && items.every(isISODate)) {
                    const times = items.map((s) => Date.parse(s));
                    return new Date(Math.min(...times));
                }

                const minStr = items.reduce<string | null>((acc, cur) => (acc === null || cur < acc ? cur : acc), null);
                return minStr;
            }

            case 'object':
                if (data[0] instanceof Date) {
                    const times = (data as Date[]).map((d) => d.getTime());
                    return new Date(Math.min(...times));
                } else {
                    return Math.min(...(data as any[]));
                }

            default:
                return data[0];
        }
    }
}
